import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, useMotionValue, useSpring, animate } from "framer-motion";
import { Sparkles, Coins, Gift, ArrowRight, Trophy, Ticket, Wallet, RotateCcw } from "lucide-react";
import PandaMascot from "@/components/PandaMascot";
import { api } from "@/lib/apiClient";

const FLOATERS = ["🍭", "🍓", "🍫", "🪙", "✨", "🍿", "🎁", "🐼"];

/**
 * Flagship homepage section — Pop Panda Club & Pop Coins.
 * - Animated background (blobs + floating fruit/popsicles)
 * - Panda mascot that reacts on scroll-in
 * - Reward cards with stagger + hover lift
 * - Animated stat counters
 * - Glowing CTAs
 */
export default function PandaClubShowcase() {
  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { once: true, margin: "-15% 0px" });
  const [mood, setMood] = useState("waiting");
  const [stats, setStats] = useState({ members: 0, coins_awarded: 0, rewards_redeemed: 0 });

  // React on scroll-in
  useEffect(() => {
    if (!inView) return;
    setMood("celebrating");
    const t1 = setTimeout(() => setMood("excited"), 1500);
    const t2 = setTimeout(() => setMood("waiting"), 3500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [inView]);

  // Fetch public stats
  useEffect(() => {
    api.get("/panda-club/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  return (
    <section
      ref={sectionRef}
      data-testid="home-panda-club"
      className="relative overflow-hidden py-24 lg:py-32"
      style={{
        background: "linear-gradient(135deg, #1a0f1f 0%, #2d1b3d 30%, #3a2318 65%, #2B2B2B 100%)",
      }}
    >
      {/* ---------- Animated background ---------- */}
      <div className="absolute inset-0 pointer-events-none z-0">
        <motion.div className="absolute -top-40 -left-20 w-[560px] h-[560px] rounded-full bg-brand-yellow/25 blur-3xl" animate={{ x: [0, 40, 0], y: [0, 30, 0] }} transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }} />
        <motion.div className="absolute top-1/3 -right-24 w-[440px] h-[440px] rounded-full bg-[#FF4F5A]/20 blur-3xl" animate={{ x: [0, -30, 0], y: [0, 40, 0] }} transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }} />
        <motion.div className="absolute bottom-0 left-1/3 w-[380px] h-[380px] rounded-full bg-[#8B5CF6]/20 blur-3xl" animate={{ x: [0, 30, 0], y: [0, -30, 0] }} transition={{ duration: 13, repeat: Infinity, ease: "easeInOut" }} />

        {/* Soft light rays */}
        <div className="absolute inset-0 opacity-30" style={{ background: "conic-gradient(from 200deg at 30% 20%, transparent 0deg, rgba(255,201,60,0.15) 60deg, transparent 120deg)" }} />

        {/* Floating fruits/popsicles */}
        {Array.from({ length: 18 }).map((_, i) => (
          <motion.span
            key={i}
            className="absolute select-none"
            style={{
              left: `${(i * 41) % 100}%`,
              top: `${(i * 59) % 100}%`,
              fontSize: 16 + ((i * 7) % 22),
            }}
            animate={{ y: [-14, 14, -14], x: [-6, 6, -6], rotate: [-6, 6, -6], opacity: [0.3, 0.75, 0.3] }}
            transition={{ duration: 7 + (i % 5), delay: i * 0.3, repeat: Infinity, ease: "easeInOut" }}
          >
            {FLOATERS[i % FLOATERS.length]}
          </motion.span>
        ))}
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-[1.05fr_1fr] gap-12 items-center">
          {/* LEFT — Text + CTAs */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-[11px] uppercase tracking-[0.32em] text-brand-yellow inline-flex items-center gap-2"
            >
              <Sparkles size={13} /> The Pop Panda Club
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-display text-5xl lg:text-6xl mt-3 leading-[1.02] text-brand-cream"
            >
              Earn <span style={{ background: "linear-gradient(90deg, #FFC93C, #FF4F5A, #FFC93C)", backgroundSize: "200% 100%", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", animation: "ppGradShift 4s ease-in-out infinite" }}>Pop Coins.</span>
              <br />Live the panda life.
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-5 text-brand-cream/75 text-lg max-w-lg leading-relaxed"
            >
              Join the Panda Club and earn Pop Coins on eligible purchases at participating Pop Panda Go stores. Redeem exciting rewards, enjoy exclusive member experiences and unlock fun surprises 🐼
            </motion.p>

            {/* Stat counters */}
            <div className="mt-8 grid grid-cols-3 gap-3 max-w-md" data-testid="panda-club-counters">
              <StatCounter inView={inView} label="Members" value={stats.members} icon={Trophy} delay={0.3} />
              <StatCounter inView={inView} label="Coins Awarded" value={stats.coins_awarded} icon={Coins} delay={0.4} />
              <StatCounter inView={inView} label="Rewards Redeemed" value={stats.rewards_redeemed} icon={Ticket} delay={0.5} />
            </div>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.6 }}
              className="mt-8 flex flex-wrap gap-3"
            >
              <GlowButton to="/member/register" primary data-testid="panda-club-cta-join">
                <Sparkles size={16} /> Join Panda Club — Free
              </GlowButton>
              <GlowButton to="/member/login" data-testid="panda-club-cta-login">Sign in</GlowButton>
              <GlowButton to="/member/spin" data-testid="panda-club-cta-spin">
                <RotateCcw size={14} /> Spin & Win
              </GlowButton>
            </motion.div>
          </div>

          {/* RIGHT — Mascot + wallet + coin orbit */}
          <div className="relative min-h-[420px] flex items-center justify-center">
            {/* Mascot */}
            <motion.div
              initial={{ scale: 0, opacity: 0, x: 40 }}
              animate={inView ? { scale: 1, opacity: 1, x: 0 } : {}}
              transition={{ type: "spring", stiffness: 140, damping: 14, delay: 0.15 }}
              className="relative z-20"
            >
              <div className="rounded-full bg-brand-cream/5 p-4 backdrop-blur-lg shadow-2xl">
                <PandaMascot mood={mood} size={220} />
              </div>
            </motion.div>

            {/* Orbiting Pop Coins around the panda */}
            <div className="absolute inset-0 pointer-events-none z-10">
              {[0, 72, 144, 216, 288].map((deg, i) => (
                <motion.div
                  key={i}
                  className="absolute top-1/2 left-1/2"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 20 + i * 3, repeat: Infinity, ease: "linear" }}
                  style={{ transformOrigin: "0 0" }}
                  initial={{ rotate: deg }}
                >
                  <motion.div
                    className="absolute h-11 w-11 rounded-full grid place-items-center font-black text-xs shadow-lg"
                    style={{
                      background: "radial-gradient(circle at 30% 30%, #FFF176, #FFC107)",
                      color: "#7a5300",
                      left: 165,
                      top: -22,
                    }}
                    animate={{ scale: [1, 1.1, 1], rotate: [-6, 6, -6] }}
                    transition={{ duration: 3 + i * 0.4, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <span>P</span>
                    <motion.span
                      className="absolute -top-1 -right-1 text-brand-yellow text-lg"
                      animate={{ opacity: [0, 1, 0], scale: [0.5, 1, 0.5] }}
                      transition={{ duration: 2, delay: i * 0.4, repeat: Infinity }}
                    >✦</motion.span>
                  </motion.div>
                </motion.div>
              ))}
            </div>

            {/* Wallet chip anchored bottom-right */}
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.7 }}
              animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ type: "spring", delay: 0.55 }}
              className="absolute bottom-6 right-2 z-30 rounded-2xl bg-white/12 border border-white/15 backdrop-blur-xl px-4 py-3 flex items-center gap-3 shadow-xl"
              data-testid="panda-club-wallet"
            >
              <div className="h-10 w-10 rounded-full bg-brand-yellow grid place-items-center"><Wallet size={18} className="text-brand-charcoal" /></div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-brand-cream/60">Your Wallet</div>
                <div className="font-display text-xl text-brand-cream leading-none">+50 <span className="text-xs text-brand-cream/60">on signup</span></div>
              </div>
            </motion.div>

            {/* Corner floating popsicles */}
            {["🍭", "🍓", "🍫"].map((e, i) => (
              <motion.span
                key={i}
                className="absolute text-4xl z-10"
                style={{ left: `${20 + i * 30}%`, top: i === 1 ? "80%" : "10%" }}
                animate={{ y: [-10, 10, -10], rotate: [-15, 15, -15] }}
                transition={{ duration: 4 + i, repeat: Infinity, ease: "easeInOut", delay: i * 0.5 }}
              >{e}</motion.span>
            ))}
          </div>
        </div>

        {/* Featured Rewards */}
        <div className="mt-20">
          <motion.div initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: 0.5 }} className="text-center mb-10">
            <div className="text-[11px] uppercase tracking-[0.28em] text-brand-yellow">Featured Rewards</div>
            <h3 className="font-display text-3xl lg:text-4xl text-brand-cream mt-2">Taste the perks</h3>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: "🍭", title: "Free Classic Popsicle", cost: 200, color: "#FFC93C" },
              { icon: "🍿", title: "Free Popcorn", cost: 120, color: "#FF9800" },
              { icon: "🎲", title: "Mystery Reward", cost: 350, color: "#8B5CF6" },
              { icon: "🎡", title: "Spin & Win", cost: 0, color: "#FF4F5A", link: "/member/spin", cta: "Spin now" },
            ].map((r, i) => (
              <RewardCard key={i} inView={inView} delay={0.15 + i * 0.08} {...r} />
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes ppGradShift { 0%, 100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }
      `}</style>
    </section>
  );
}

// -------- Sub components --------

function StatCounter({ inView, value, label, icon: Icon, delay }) {
  const mv = useMotionValue(0);
  const spring = useSpring(mv, { stiffness: 60, damping: 20 });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(mv, value || 0, { duration: 1.6, ease: "easeOut", delay });
    const unsub = spring.on("change", (v) => setDisplay(Math.floor(v)));
    return () => { controls.stop(); unsub(); };
    // eslint-disable-next-line
  }, [inView, value]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay, duration: 0.5 }}
      className="rounded-2xl bg-white/8 border border-white/15 backdrop-blur-xl p-4"
    >
      <div className="text-[10px] uppercase tracking-widest text-brand-yellow inline-flex items-center gap-1"><Icon size={11} /> {label}</div>
      <div className="mt-1 font-display text-2xl text-brand-cream leading-none">{formatN(display)}</div>
    </motion.div>
  );
}

function formatN(n) {
  if (n >= 1e6) return (n / 1e6).toFixed(1).replace(/\.0$/, "") + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(1).replace(/\.0$/, "") + "K";
  return String(n);
}

function RewardCard({ inView, delay, icon, title, cost, color, link, cta }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay, duration: 0.6, ease: "easeOut" }}
      whileHover={{ y: -8, scale: 1.02 }}
      className="relative rounded-3xl bg-white/6 border border-white/12 backdrop-blur-2xl p-6 overflow-hidden group cursor-default"
      data-testid={`panda-club-reward-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
    >
      {/* Ambient glow on hover */}
      <div className="absolute -inset-1 opacity-0 group-hover:opacity-40 blur-xl transition-opacity duration-500" style={{ background: `radial-gradient(circle at center, ${color}, transparent 70%)` }} />

      <div className="relative">
        <motion.div
          className="text-5xl"
          animate={{ y: [-4, 4, -4], rotate: [-6, 6, -6] }}
          transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
        >{icon}</motion.div>
        <div className="mt-4 font-display text-xl text-brand-cream leading-tight">{title}</div>
        {cost > 0 ? (
          <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-brand-yellow/15 border border-brand-yellow/25 px-3 py-1 text-xs text-brand-yellow">
            <Coins size={12} /> {cost}
          </div>
        ) : (
          <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-brand-yellow/15 border border-brand-yellow/25 px-3 py-1 text-xs text-brand-yellow">
            <Sparkles size={12} /> Play now
          </div>
        )}

        {link && (
          <Link to={link} className="mt-4 inline-flex items-center gap-1 text-xs text-brand-cream/80 hover:text-brand-cream group">
            {cta || "View"} <ArrowRight size={12} className="transition-transform group-hover:translate-x-0.5" />
          </Link>
        )}
      </div>
    </motion.div>
  );
}

function GlowButton({ to, children, primary, ...rest }) {
  return (
    <Link
      to={to}
      {...rest}
      className={`relative group rounded-full px-6 py-3 text-sm font-medium inline-flex items-center gap-2 overflow-hidden ${
        primary ? "bg-brand-yellow text-brand-charcoal shadow-[0_8px_24px_rgba(255,201,60,0.35)] hover:shadow-[0_10px_36px_rgba(255,201,60,0.55)]" :
        "bg-white/10 border border-white/20 text-brand-cream backdrop-blur-xl hover:bg-white/20"
      } transition-all duration-300`}
    >
      {/* Ripple / shimmer on hover */}
      <span
        aria-hidden
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
        style={{
          background: primary
            ? "radial-gradient(circle at var(--x, 50%) var(--y, 50%), rgba(255,255,255,0.6), transparent 55%)"
            : "radial-gradient(circle at var(--x, 50%) var(--y, 50%), rgba(255,255,255,0.25), transparent 55%)",
        }}
      />
      <span className="relative z-10 inline-flex items-center gap-2 group-hover:scale-[1.02] transition-transform">{children}</span>
    </Link>
  );
}
